import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom'; // 需求 R99：預覽 modal 以 createPortal 掛到 document.body

const API = process.env.REACT_APP_API_URL || '/api';

/**
 * 需求 R86 螢幕截圖上傳（PM 2026-07-29 留言）
 *
 * PM 提問：「瀏覽器彈出原生『選擇分享來源』對話框，這裡若是選擇後還可以自己截取
 *          該對話框要上傳的範圍嗎？」
 *
 * 技術事實：
 *  - navigator.mediaDevices.getDisplayMedia() 的原生對話框只能三選一（整個螢幕／
 *    某視窗／某分頁），對話框「本身」無法框選局部範圍，一定是整張來源畫面。
 *  - 因此「選來源後再框選要上傳的範圍」無法在原生對話框內完成，但可以在我們自己的
 *    App 內做：取得整張影格後畫到 canvas，讓使用者在預覽上拖曳一個矩形框選要保留
 *    的區域，只裁切該區域上傳。此元件即實作此流程，直接回答 PM 的問題＝可以，只是
 *    框選發生在「截取後的預覽」而非原生對話框。
 *
 * 流程：截圖按鈕 → getDisplayMedia 取一張影格 → 預覽（預設整張全選、可拖曳框選局部）
 *      → 〔取消／重新截取／確認〕→ 確認＝裁切選取範圍 → 沿用既有 /products/upload-image
 *      端點上傳（後端零改動、一樣 sharp 壓縮 300KB）→ 回填 productImage。
 *
 * 相容性（AC7 / D3）：iOS Safari 與 Android Chrome 不支援 getDisplayMedia，
 *      故本元件偵測不支援時回傳 null（不渲染按鈕），呼叫端沿用既有檔案上傳。
 *
 * ── PM 2026-07-29 追加回饋
 * 回饋①：「選取完要點選的視窗後，後續流程會造成困惑，不確定是否已完成截取，因為畫面
 *        會停留在選取的視窗，要點回原本網頁視窗才發現已完成。是否可讓視窗轉換更直覺，
 *        例如直接跳回。」
 * 根因：getDisplayMedia 讓使用者在原生對話框選「其他視窗」當來源後，作業系統焦點會停在
 *        那個被截視窗上；我們雖已抓到影格並跳出預覽 modal，但 modal 在背景的瀏覽器視窗裡，
 *        使用者看不到，才會「不確定是否截好」。
 * 對策「直接跳回」：抓到影格後立即 window.focus() 嘗試把焦點帶回瀏覽器視窗；並先停止
 *        分享串流（結束系統的「正在分享」狀態）再 focus，讓瀏覽器更容易回到前景。預覽
 *        modal 也明確標示「 已截取」，消除「不知道好了沒」的疑慮。
 *
 * ── PM 2026-07-29 追加回饋
 * 回饋②：「若『直接跳回』使用者立刻看到預覽，那就不需要『 貼上截圖』按鈕，請取消，
 *        維持螢幕截圖、圖片上傳兩種方式上傳圖片即可。」
 * 處置：既然「直接跳回」已讓使用者立即看到預覽，剪貼簿貼上路徑已非必要 → 移除「 貼上
 *        截圖」按鈕、pasteFromClipboard、全域 Ctrl/⌘+V 貼上監聽與相關提示文字。本元件
 *        僅保留「 螢幕截圖」一種入口；檔案上傳由呼叫端既有 <input type=file> 提供，
 *        故最終維持「螢幕截圖 + 圖片上傳」兩種方式（AC1 檔案上傳不受影響）。
 *
 * ── PM 2026-07-29 最新回饋（本次 commit）
 * 回饋③：「既然採取『選擇視窗』擷取整個畫面、再讓使用者框選範圍，是否可以直接取消『跳出
 *        被選取的畫面』，使用者直接在網頁中看到截取畫面。」
 * 技術限制（誠實說明）：純網頁無法阻止「原生對話框選視窗當來源」時作業系統把那個被截視窗
 *        帶到前景——這一步是 OS／瀏覽器層行為，網頁沙箱管不到。網頁能做的是「讓停留在被
 *        選取畫面的時間縮到最短、並盡快把焦點帶回瀏覽器」。
 * 對策（本次 commit）：
 *   ① 影格一就緒即抓：改用 video.requestVideoFrameCallback（不支援則短輪詢）在第一張畫面
 *      可用的當下就抓一張，不再固定等 150ms，縮短停在被選取視窗的時間。
 *   ② 抓到影格後「立刻」stop 串流結束系統「正在分享」狀態，作業系統多半會把焦點還給原本
 *      在前景的瀏覽器；再 window.focus() 盡力把瀏覽器帶回前景，使用者一轉頭就看到預覽。
 *   ③ getDisplayMedia 帶 Chromium 專屬提示 surfaceSwitching/selfBrowserSurface='exclude'
 *      （其他瀏覽器忽略），減少分享期間的來源切換列干擾。
 *   → 效果：把「跳出被選取畫面」壓到近乎瞬間、並自動跳回網頁看到截取畫面；但無法 100% 保證
 *      每個瀏覽器都能程式取回焦點（見上方技術限制），此為純網頁能力上限。
 *
 * ── PM 2026-07-29 回饋④（已於下方回饋⑤撤銷，保留紀錄）
 * 回饋④曾要求：「截圖功能在畫面選擇上是否可以只停留當下畫面就好，不需選擇視窗／畫面了？」
 * 當時對策：getDisplayMedia 帶 `preferCurrentTab: true`＋`displaySurface:'browser'`，讓 Chromium
 *   略過「選擇分享來源」清單，只擷取當下這個網頁分頁。
 *
 * ── PM 2026-07-29 回饋⑤（本次 commit：撤銷回饋④）
 * 回饋⑤：「我只是想先知道這個過程，並非要改成這樣的方式，目前看來已做了調整，需要請你取消
 *        這個只停留當下畫面的更動。」
 * 處置（本次 commit）：回饋④是 PM 探詢流程時我方過度解讀成需求並實作，PM 澄清並不要此行為。
 *   → 移除 `preferCurrentTab: true` 與 `displaySurface:'browser'`，getDisplayMedia 還原為
 *     `video: true`＋原生「選擇分享來源」對話框（整個螢幕／某視窗／某分頁三選一），
 *     並復原 Chromium 專屬的 surfaceSwitching/selfBrowserSurface/systemAudio='exclude' 提示。
 *   → 效果：回到回饋③（commit 6da94af）的行為：使用者自行在原生對話框選擇要截取的畫面，
 *     截取後在 App 內預覽、框選、確認上傳；不再強制「只停留當下網頁分頁」。
 *
 * ── PM 2026-07-29 回饋⑥（本次 commit）
 * 回饋⑥：「感謝取消只停留當下網頁分頁，但請執行讓截取的畫面變成是 Entire Screen，
 *        不需要選擇其他視窗。」
 * 對策（本次 commit）：getDisplayMedia 的 video 帶 `displaySurface: 'monitor'`，並加
 *   `monitorTypeSurfaces: 'include'`，讓 Chromium 系瀏覽器在原生「選擇分享來源」對話框
 *   **預設把來源型別鎖定／預選為「整個螢幕 Entire Screen」**，使用者不必再切到「視窗／
 *   分頁」清單挑選，直接確認即擷取整個螢幕畫面。
 *   誠實限制（純網頁上限）：瀏覽器安全規範仍要求使用者在對話框按一次「分享」確認鍵，
 *     純網頁無法做到完全零點擊靜默截取；displaySurface 為「提示」，Firefox／Safari 不支援
 *     時會自動忽略、退回原生完整來源選單（graceful fallback）。此提示與 selfBrowserSurface
 *     語意不衝突，故沿用 surfaceSwitching/selfBrowserSurface/systemAudio='exclude'。
 *
 * ── PM 2026-07-29 回饋⑦（本次 commit）
 * 回饋⑦：「希望點選截圖按鈕就直接是整個螢幕的截圖顯示，如同之前過度解讀成需求的
 *        只停留在當下畫面一樣的呈現，是否可行？」（＝這是一個「可行度」提問）
 * 誠實可行度回答（不新增未經拍板的行為，避免重演回饋④→⑤的過度解讀）：
 *   • 「當下畫面（preferCurrentTab）」之所以能『免對話框、直接截』，是因為它只截「使用者
 *     自己的這個分頁」，隱私風險最低，瀏覽器才允許略過選來源清單——這正是回饋④做過、
 *     回饋⑤要求撤銷的行為。
 *   • 「整個螢幕 Entire Screen」屬於高隱私風險來源，瀏覽器安全模型**強制**必須跳出原生
 *     「選擇分享來源」對話框、並由使用者按一次「分享」確認，**沒有任何純網頁 API 能預先
 *     確認或略過這一步**。因此「整個螢幕」無法做到像 preferCurrentTab 那樣完全免點擊。
 *   • 目前（commit a6dae99）已用 `displaySurface:'monitor'`＋`monitorTypeSurfaces:'include'`
 *     把對話框**預選為「整個螢幕」**，使用者不必再切視窗／分頁清單，只要按一次「分享」即
 *     擷取整個螢幕——這已是純網頁能達到的**最直接**程度。
 * 本次 commit 實作：在此誠實記錄可行度結論，並把按鈕提示（title）改為引導「按一次分享即
 *   擷取整個螢幕（已預選整個螢幕）」，讓「直接截整個螢幕」的操作更清楚；不改動截取行為、
 *   不引入 preferCurrentTab（避免重演過度解讀）。若 PM 需要「完全免點擊」的整個螢幕截取，
 *   則超出純網頁範圍，須改桌面 App／Electron 或瀏覽器擴充功能（範圍與成本不同），待 PM 拍板。
 *
 * ── 需求 R100（PM 2026-08-03 拍板 Q1-Q6，本次 commit）
 * 需求：「選擇整個螢幕之後的截圖畫面可以再放大嗎？」附圖顯示截取後的預覽視窗把整張螢幕
 *       縮得極小，文字看不清、要拖曳框選要上傳的範圍時框不準。
 * 根因（不是可視空間不夠）：舊版 displaySize 由兩個寫死常數卡住 ——
 *       maxW = Math.min(720, innerWidth - 80)、maxH = Math.min(520, innerHeight - 220)。
 *       外層白框明明已允許 maxWidth:'90vw' / maxHeight:'90vh'，但 720/520 與螢幕大小無關，
 *       在 1512×982 CSS 視窗截 2560×1440 時預覽只有 720×405＝原圖的 28%，1 個預覽像素
 *       等於原圖 3.6 像素，框選誤差本來就會被放大 3.6 倍。
 * PM 拍板：Q1=A（單純放大吃滿可視範圍，不做縮放控制）、Q2=上限用「可視範圍扣掉標題與
 *       按鈕列」即可、Q3=維持不放大（scale ≤ 1，原圖小於預覽框時不硬放大以免糊）、
 *       Q4=不需要 1:1 檢視＋捲動、Q5=不需要即時顯示選取範圍原始像素尺寸、
 *       Q6=toDataURL 效能問題請先說明（本次不動，等 PM 看完說明再決定）。
 * 本次實作：移除 720/520，改由 modal 實際結構反推可用範圍（見下方 previewMaxBox），
 *       並補上 window resize 重算（舊版只在 frame 改變時算一次，中途縮放視窗會破版）；
 *       resize 時既有框選以等比換算跟著縮放，避免框錯位。
 * 不影響上傳畫質：confirm() 一律以 displaySize 與原圖的比例把框選座標換算回原始
 *       frame.canvas 再裁切，預覽純粹是顯示層；放大只讓框選更精準，輸出解析度不變。
 *
 * ── 需求 R99（PM 2026-08-03 拍板 Q1/Q3/Q5，本次 commit）
 * 現象（PM 兩張截圖）：從縮圖左下角 （compact）截圖後跳出的預覽視窗被頁面元素干擾——
 *   ① 上方導覽列與表格置頂表頭畫在預覽視窗「之上」，而且沒有被半透明遮罩變暗；
 *   ② 預覽左上角「 已截取畫面」與「在畫面上按住拖曳…」兩行提示疊在同一行、溢出白框；
 *   ③ 預覽開著時底下縮圖仍冒出「更換圖片」hover 遮罩與「點擊更新產品圖片」原生提示。
 * 根因（三個現象同一個源頭）：需求 R88 把本元件（含預覽 modal）塞進縮圖角落那顆按鈕的
 *   <span> 裡，而該 span 的 `position:absolute; z-index:3; line-height:0`
 *   （QuoteCreatePage.css:474 / ProductsPage.css:1121 / CatalogPage.css:601）會污染 modal：
 *   ① z-index:3 建立 stacking context → modal 的 position:fixed; z-index:1200 只在這個
 *      context「內部」有效，對外整包只等於 z-index:3。頁面上 z-index > 3 的元素全都會蓋過去：
 *      .page-header(Header.css:7, 200)、.qc-table thead(QuoteCreatePage.css:357, 4, sticky)、
 *      .products-data-table thead(ProductsPage.css:614, 4, sticky)、ProductsPage 置左固定欄(5)。
 *      這正好對上截圖裡「只有導覽列與表頭沒有變暗」的現象。
 *   ② line-height:0 被 modal 內所有文字繼承 → 兩行提示行高變 0 而重疊、溢出白框。
 *   ③ modal 是 .product-thumb-wrapper 的 DOM 子孫 → 滑鼠移到 modal 上等於 hover 該縮圖，
 *      CSS `:hover .product-thumb-edit-overlay` 與 wrapper 的原生 title 提示照樣冒出來。
 * PM Q1=A（本次採用）：預覽 modal 改用 ReactDOM.createPortal(..., document.body)。一次解掉
 *   三個根因——跳出所有 stacking context（body 底下只跟 z-index 比大小）、不再繼承
 *   line-height:0（CSS 繼承走 DOM 樹）、不再是縮圖的 DOM 子孫（hover/title 不外洩）；
 *   而且日後任何頁面新增高 z-index 元素也不會再蓋住預覽。
 * PM Q3=三處一起修：報價單建立（QuoteCreatePage.js:1633）／產品管理批量（ProductsPage.js:1277）
 *   ／型錄批量（CatalogPage.js:1612）共用本元件，改這一支即三處全修，呼叫端零改動。
 * PM Q2「建議為何？」→ 建議維持字面值 1200，不要改吃 var(--z-index-modal)。理由：
 *   本專案 --z-index-modal 只有 400（styles/variables.css:52），但實際上已經有比它高的層——
 *   公司 Modal 1000（CatalogPage.css:1165）、ProductsPage 內嵌 modal 1000（ProductsPage.js:1503）、
 *   報價單 toast 1100（QuoteCreatePage.css:506）。改吃 400 會把截圖預覽壓到這些之下，等於用
 *   一個新回歸換掉舊回歸；且本元件自 需求 R88 起樣式一律 inline（刻意不依賴頁面 CSS 以避開
 *   stale build），inline style 也讀不到 CSS 變數。故改為抽出具名常數 PREVIEW_MODAL_Z（見下方）
 *   並在該處列出全站完整層級階梯，維持 1200 ＝ 目前最高，語意比散落的 magic number 清楚。
 * PM Q4（hover 遮罩／title 提示於 modal 開啟時消失是否即為所要）：PM 本輪未明確回覆。方案 A
 *   會讓兩者自動消失，本次即以此行為交付，並於評論請 PM 確認；若 PM 要保留，再另行處理。
 * PM Q5：QuoteEditPage 補縮圖截圖鈕＝PM 指示「另開案執行」，本票不做。
 * 回歸保護（AC6）：呼叫端的 <span onClick={e => e.stopPropagation()}> 在 **React 樹** 上仍是
 *   modal 的祖先，而 React 合成事件是沿 React 樹（而非 DOM 樹）冒泡，portal 不改變這點——
 *   故「取消／重新截取／確認」的點擊照樣被該 span 攔下，不會觸發外層縮圖的檔案上傳。
 */

const supportsScreenCapture = () =>
  typeof navigator !== 'undefined' &&
  navigator.mediaDevices &&
  typeof navigator.mediaDevices.getDisplayMedia === 'function';

// 影格一就緒即抓一張（優先 requestVideoFrameCallback，退回短輪詢），
// 讓「結束分享→跳回網頁」越快發生越好，縮短停在被選取視窗的時間。
const grabFirstFrame = (video) => new Promise((resolve) => {
  const draw = () => {
    const w = video.videoWidth, h = video.videoHeight;
    if (!w || !h) { setTimeout(draw, 30); return; } // 影像尚未就緒，稍後再試
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);
    resolve({ canvas, width: w, height: h });
  };
  if (typeof video.requestVideoFrameCallback === 'function') {
    video.requestVideoFrameCallback(() => draw());
  } else {
    setTimeout(draw, 120);
  }
});

// 需求 R100：預覽最大可用範圍。這些常數對應下方預覽 modal 的實際版面，不再有「與螢幕
// 大小無關的固定上限」（PM Q2：可視範圍扣掉標題與按鈕列即可）。
const OVERLAY_PADDING = 20; // 遮罩層的 padding（上下左右各 20，見預覽 modal 外層 style）
const BOX_PADDING = 16;     // 白色 modal 本身的 padding
const HEADER_H = 26;        // 「✓ 已截取畫面」14px 粗體一行 + marginBottom 4
const HINT_H = 30;          // 「在畫面上按住拖曳…」13px 一行 + marginBottom 8
const FOOTER_H = 62;        // 按鈕列：marginTop 12 + 按鈕高度約 40 + 誤差
const MIN_PREVIEW = 200;    // 極小視窗的下限，避免算出 0 或負數把預覽壓沒

// 白框本身受 maxWidth:'90vw' / maxHeight:'90vh' 與外層 padding 兩者夾擊，取較小者；
// 再扣掉白框內距與上下三列（標題、說明、按鈕）才是圖片真正能用的範圍。
const previewMaxBox = () => {
  const boxW = Math.min(window.innerWidth * 0.9, window.innerWidth - OVERLAY_PADDING * 2);
  const boxH = Math.min(window.innerHeight * 0.9, window.innerHeight - OVERLAY_PADDING * 2);
  return {
    maxW: Math.max(MIN_PREVIEW, Math.floor(boxW - BOX_PADDING * 2)),
    maxH: Math.max(MIN_PREVIEW, Math.floor(boxH - BOX_PADDING * 2 - HEADER_H - HINT_H - FOOTER_H)),
  };
};

// 需求 R99 Q2：預覽 modal 的疊放層級。全站現有層級階梯（由低到高）：
// sticky thead 4 → ProductsPage 置左固定欄 5 → .page-header 200 → var(--z-index-modal) 400
// → 公司 Modal / ProductsPage 內嵌 modal 1000 → 報價單 toast 1100 → 本預覽 1200（最高）。
// 維持 1200 字面值而非 var(--z-index-modal)(=400) 的理由見上方檔頭 Q2。
const PREVIEW_MODAL_Z = 1200;

// 需求 R87：fullWidth 讓「 螢幕截圖」按鈕滿版（與上方上傳區塊等寬）。
// 僅 ProductDetailPage 傳入 true；ProductModal 不傳，維持既有自動寬度版面（AC5 共用元件不破壞）。
//
// 需求 R88（PM 2026-07-30）：把截圖上傳延伸到「報價單品項清單縮圖」與
// 「型錄批量修改圖片格」兩處。這兩處需要的是疊在圖片框左下角的「角落小圖示」而非滿版按鈕，
// 故新增 compact 變體：只渲染一顆 icon-only 小按鈕（外觀用 inline style 自帶，不依賴頁面
// CSS，避免 stale build 影響），並在 onClick 先 stopPropagation，避免同時觸發外層縮圖既有的
// 「點擊更換圖片（檔案上傳）」行為（AC5）。截取／裁切／上傳／回填核心邏輯與滿版版本共用，
// 截取行為（displaySurface:'monitor' 整個螢幕、按一次分享、可框選）不變（見上方回饋⑥⑦）。
const ScreenshotCapture = ({ onUploaded, fullWidth = false, compact = false }) => {
  const [frame, setFrame] = useState(null); // { canvas, width, height }
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [sel, setSel] = useState(null); // { x, y, w, h } in display px (null = whole image)
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const dragStart = useRef(null);
  const previewRef = useRef(null);
  const sizeRef = useRef({ width: 0, height: 0 }); // 需求 R100：resize 前的預覽尺寸，供框選等比換算

  const supported = supportsScreenCapture();

  // 擷取一張螢幕影格
  const capture = useCallback(async () => {
    setError('');
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        // PM 回饋⑥：截取「整個螢幕 Entire Screen」，不需選擇其他視窗。
        // displaySurface:'monitor' 讓 Chromium 在原生對話框預選「整個螢幕」型別；
        // 不支援的瀏覽器（Firefox／Safari）自動忽略、退回完整來源選單（graceful fallback）。
        video: { displaySurface: 'monitor' },
        audio: false,
        // Chromium 專屬提示（其他瀏覽器忽略）：優先/鎖定「整個螢幕」型別，減少視窗／分頁
        // 來源造成的困惑，讓「選整個螢幕→跳回網頁」流程更乾淨。
        monitorTypeSurfaces: 'include',
        surfaceSwitching: 'exclude',
        selfBrowserSurface: 'exclude',
        systemAudio: 'exclude',
      });
    } catch (err) {
      // 使用者在原生對話框按取消會拋 NotAllowedError，視為放棄，不顯示錯誤
      if (err && err.name === 'NotAllowedError') return;
      setError('無法啟動螢幕擷取，請確認瀏覽器權限');
      return;
    }
    try {
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      // PM 回饋③：影格一就緒即抓（不再固定等 150ms），縮短停在被選取視窗的時間
      const f = await grabFirstFrame(video);
      // 立刻結束分享 → 系統結束「正在分享」狀態後，作業系統多半會把焦點還給原本在前景
      // 的瀏覽器；緊接著 window.focus() 盡力把瀏覽器帶回前景，讓使用者直接在網頁看到截圖，
      // 不必停在被選取的畫面（純網頁能做到的最大程度，OS 層焦點切換無法 100% 保證）。
      stream.getTracks().forEach((t) => t.stop());
      try { window.focus(); } catch (e) { /* 忽略：瀏覽器可能不允許程式取回焦點 */ }
      setFrame(f);
      setSel(null); // 預設整張全選（可直接確認整張）
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      setError('擷取畫面失敗，請重試');
    }
  }, []);

  // 依預覽容器可用範圍換算顯示尺寸（需求 R100：吃滿可視範圍，不再用 720/520 寫死上限）
  useEffect(() => {
    if (!frame) return undefined;
    const apply = () => {
      const { maxW, maxH } = previewMaxBox();
      // Q3 拍板：維持 scale ≤ 1，截圖原圖比預覽框小時不放大（硬放大只會糊）
      const scale = Math.min(maxW / frame.width, maxH / frame.height, 1);
      const next = { width: Math.round(frame.width * scale), height: Math.round(frame.height * scale) };
      const prev = sizeRef.current;
      // 視窗縮放後預覽尺寸會變，既有框選是「舊的 display px」，等比換算才不會錯位；
      // confirm() 用當下 displaySize 反推原圖座標，兩者同步才不會裁到錯的位置。
      if (prev.width && prev.height && next.width && next.height
          && (prev.width !== next.width || prev.height !== next.height)) {
        const rx = next.width / prev.width;
        const ry = next.height / prev.height;
        setSel((cur) => (cur ? { x: cur.x * rx, y: cur.y * ry, w: cur.w * rx, h: cur.h * ry } : cur));
      }
      sizeRef.current = next;
      setDisplaySize(next);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [frame]);

  const closeAll = () => {
    setFrame(null);
    setSel(null);
    sizeRef.current = { width: 0, height: 0 }; // 需求 R100：下次截取重新量，不帶著上一張的尺寸
    setDragging(false);
    setError('');
  };

  // 拖曳框選（座標為預覽 display px）
  const pointFromEvent = (e) => {
    const rect = previewRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: Math.max(0, Math.min(displaySize.width, clientX - rect.left)),
      y: Math.max(0, Math.min(displaySize.height, clientY - rect.top)),
    };
  };
  const onDown = (e) => {
    e.preventDefault();
    const p = pointFromEvent(e);
    dragStart.current = p;
    setDragging(true);
    setSel({ x: p.x, y: p.y, w: 0, h: 0 });
  };
  const onMove = (e) => {
    if (!dragging || !dragStart.current) return;
    const p = pointFromEvent(e);
    const s = dragStart.current;
    setSel({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const onUp = () => {
    setDragging(false);
    // 太小的框視為誤觸 → 還原成整張
    setSel((cur) => (cur && (cur.w < 8 || cur.h < 8) ? null : cur));
  };

  // 確認：裁切選取範圍（未框選＝整張）→ 上傳
  const confirm = async () => {
    if (!frame) return;
    setUploading(true);
    setError('');
    try {
      const scaleX = frame.width / (displaySize.width || frame.width);
      const scaleY = frame.height / (displaySize.height || frame.height);
      let sx = 0, sy = 0, sw = frame.width, sh = frame.height;
      if (sel && sel.w >= 8 && sel.h >= 8) {
        sx = Math.round(sel.x * scaleX);
        sy = Math.round(sel.y * scaleY);
        sw = Math.round(sel.w * scaleX);
        sh = Math.round(sel.h * scaleY);
      }
      const out = document.createElement('canvas');
      out.width = sw;
      out.height = sh;
      out.getContext('2d').drawImage(frame.canvas, sx, sy, sw, sh, 0, 0, sw, sh);
      const blob = await new Promise((res) => out.toBlob(res, 'image/jpeg', 0.92));
      if (!blob) { setError('產生圖片失敗'); setUploading(false); return; }
      const formData = new FormData();
      formData.append('image', new File([blob], `screenshot-${sw}x${sh}.jpg`, { type: 'image/jpeg' }));
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/products/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const { url } = await res.json();
        onUploaded(url);
        closeAll();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.message || '上傳失敗');
      }
    } catch (err) {
      console.error('截圖上傳失敗', err);
      setError('截圖上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  if (!supported) return null; // D3：手機／不支援的瀏覽器不渲染，沿用檔案上傳

  return (
    <>
      {compact ? (
        // 需求 R88：角落小圖示（icon-only）。外觀 inline style 自帶、不依賴頁面 CSS；
        // 由呼叫端以絕對定位把本按鈕擺到圖片框左下角。onClick 先 stopPropagation，避免
        // 觸發外層縮圖既有的「點擊更換圖片（檔案上傳）」行為（AC5）。
        <button
          type="button"
          className="screenshot-capture-compact-btn"
          onClick={(e) => { e.stopPropagation(); capture(); }}
          title="螢幕截圖上傳：對話框已預選『整個螢幕』，按一次『分享』即擷取（可於截取後框選要上傳的範圍）"
          aria-label="螢幕截圖上傳"
          style={{
            width: 20, height: 20, padding: 0, border: '1px solid rgba(255,255,255,0.7)',
            borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11,
            lineHeight: '18px', cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
          }}
        >
          📷
        </button>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 6 }}>
          <button
            type="button"
            className="btn btn-outline"
            style={{ padding: '6px 12px', fontSize: 13, ...(fullWidth ? { width: '100%' } : {}) }}
            onClick={capture}
            title="截取整個螢幕：對話框已預選『整個螢幕』，按一次『分享』即擷取（可於截取後框選要上傳的範圍）"
          >
            📷 螢幕截圖
          </button>
        </div>
      )}
      {error && !frame && !compact && (
        <div style={{ color: 'var(--color-danger, #d33)', fontSize: 13, marginTop: 4 }}>{error}</div>
      )}

      {/* 需求 R99 Q1=A：預覽 modal 一律 portal 到 document.body。留在原地（縮圖角落那顆
          z-index:3 / line-height:0 的 span 裡）會被 stacking context 困住而遭導覽列與置頂
          表頭蓋住、文字行高被繼承成 0、還會外洩縮圖的 hover 遮罩與 title 提示。 */}
      {frame && ReactDOM.createPortal((
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: PREVIEW_MODAL_Z,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            // portal 之後已不會再繼承角落 span 的 line-height:0，這裡再明確指定一次當保險，
            // 讓本元件日後被掛進任何 line-height:0 容器內也不會再出現兩行字疊在一起。
            lineHeight: 'normal',
          }}
          onMouseUp={onUp}
          onTouchEnd={onUp}
        >
          <div style={{ background: '#fff', borderRadius: 8, padding: 16, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-success, #2e7d32)', marginBottom: 4 }}>
              ✓ 已截取畫面
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
              在畫面上按住拖曳可框選要上傳的範圍；不框選則上傳整張。確認後才會上傳。
            </div>
            <div
              ref={previewRef}
              style={{ position: 'relative', width: displaySize.width, height: displaySize.height, cursor: 'crosshair', userSelect: 'none', touchAction: 'none' }}
              onMouseDown={onDown}
              onMouseMove={onMove}
              onTouchStart={onDown}
              onTouchMove={onMove}
            >
              <img
                src={frame.canvas.toDataURL('image/jpeg', 0.85)}
                alt="screenshot preview"
                draggable={false}
                style={{ width: displaySize.width, height: displaySize.height, display: 'block', borderRadius: 4 }}
              />
              {sel && sel.w > 0 && sel.h > 0 && (
                <div
                  style={{
                    position: 'absolute', left: sel.x, top: sel.y, width: sel.w, height: sel.h,
                    border: '2px dashed #2d7ef7', background: 'rgba(45,126,247,0.15)', pointerEvents: 'none',
                  }}
                />
              )}
            </div>
            {error && <div style={{ color: 'var(--color-danger, #d33)', fontSize: 13, marginTop: 8 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" className="btn btn-secondary" onClick={closeAll} disabled={uploading}>取消</button>
              <button type="button" className="btn btn-secondary" onClick={capture} disabled={uploading}>重新截取</button>
              <button type="button" className="btn btn-primary" onClick={confirm} disabled={uploading}>
                {uploading ? '上傳中…' : '確認'}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </>
  );
};

export default ScreenshotCapture;
